import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { getDocument } from "pdfjs-dist";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

interface AssetFile {
  source: string;
  name?: string;
  contentType?: string;
}

interface HighlightPosition {
  x: number;
  y: number;
  page?: number;
}

interface AssetViewerPaneProps {
  title: string;
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  reviewStatus?: string | null;
}

export interface AssetViewerPaneHandle {
  navigateToPage: (page: number) => void;
}

function isPdf(asset: AssetFile): boolean {
  if (asset.contentType?.includes("pdf")) return true;
  if (asset.name?.toLowerCase().endsWith(".pdf")) return true;
  if (asset.source?.toLowerCase().endsWith(".pdf")) return true;
  return false;
}

function isImage(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("image/")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(ext);
}

/** Full-pane PDF renderer with page navigation and highlight overlay */
function PdfViewer({
  url,
  highlightPosition,
  onPageChange,
}: {
  url: string;
  highlightPosition?: HighlightPosition | null;
  onPageChange?: (setter: (page: number) => void) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);

  // Expose page navigation to parent
  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    });
  }, [onPageChange, totalPages]);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      const scale = 1.5;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) return;

      await page.render({ canvasContext: context, viewport }).promise;
    } catch (err) {
      console.error("Error rendering PDF page:", err);
      setError("Failed to render page");
    } finally {
      setRendering(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        const pdfDoc = await getDocument(url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        await renderPage(1);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [url, renderPage]);

  useEffect(() => {
    if (pdfDocRef.current && currentPage > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, renderPage]);

  // Navigate to highlight page when position changes
  useEffect(() => {
    if (highlightPosition?.page && highlightPosition.page !== currentPage) {
      setCurrentPage(highlightPosition.page);
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text size="2" className="text-puntt-neutral-gray-9">
          {error}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2">
        {rendering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Spinner size="2" />
          </div>
        )}
        <div className="relative inline-block">
          <canvas
            ref={canvasRef}
            className="mx-auto block"
            style={{ maxWidth: "100%" }}
          />
          {/* Highlight marker */}
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentPage) && (
              <div
                className="pointer-events-none absolute z-20 size-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
        >
          <Button
            size="1"
            variant="ghost"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="cursor-pointer"
          >
            <CaretLeft size={14} />
          </Button>
          <Text size="1" className="text-puntt-neutral-gray-11">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            size="1"
            variant="ghost"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="cursor-pointer"
          >
            <CaretRight size={14} />
          </Button>
        </Flex>
      )}
    </div>
  );
}

/** Image viewer with highlight overlay and pagination */
function ImageViewer({
  assets,
  highlightPosition,
}: {
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalPages = assets.length;
  const asset = assets[currentIndex] ?? assets[0];

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2">
        <div className="relative">
          <img
            src={asset.source}
            alt={asset.name ?? `Image ${currentIndex + 1}`}
            className="mx-auto block max-w-full object-contain"
          />
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentIndex + 1) && (
              <div
                className="pointer-events-none absolute z-20 size-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
        >
          <Button
            size="1"
            variant="ghost"
            disabled={currentIndex <= 0}
            onClick={() => setCurrentIndex((p) => Math.max(0, p - 1))}
            className="cursor-pointer"
          >
            <CaretLeft size={14} />
          </Button>
          <Text size="1" className="text-puntt-neutral-gray-11">
            Page {currentIndex + 1} of {totalPages}
          </Text>
          <Button
            size="1"
            variant="ghost"
            disabled={currentIndex >= totalPages - 1}
            onClick={() =>
              setCurrentIndex((p) => Math.min(totalPages - 1, p + 1))
            }
            className="cursor-pointer"
          >
            <CaretRight size={14} />
          </Button>
        </Flex>
      )}
    </div>
  );
}

export const AssetViewerPane = forwardRef<
  AssetViewerPaneHandle,
  AssetViewerPaneProps
>(function AssetViewerPane(
  { title, assets, highlightPosition, reviewStatus },
  ref,
) {
  const [activeTab, setActiveTab] = useState(0);
  const pageNavigatorRef = useRef<((page: number) => void) | null>(null);

  useImperativeHandle(ref, () => ({
    navigateToPage(page: number) {
      pageNavigatorRef.current?.(page);
    },
  }));

  if (assets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          No {title.toLowerCase()} files
        </Text>
      </div>
    );
  }

  const currentAsset = assets[activeTab] ?? assets[0];
  const allImages = assets.length > 1 && assets.every((a) => isImage(a));
  const showTabs = assets.length > 1 && !allImages;

  return (
    <div className="flex h-full flex-col bg-base-white">
      {/* Header with title and optional tabs */}
      <div className="shrink-0 border-b border-puntt-neutral-gray-5 px-4 py-2">
        <Text
          size="1"
          weight="bold"
          className="mb-1 block uppercase tracking-wide text-puntt-neutral-gray-9"
        >
          {title}
        </Text>
        {reviewStatus && (
          <Text size="1" className="animate-pulse italic text-puntt-accent-9">
            {reviewStatus}
          </Text>
        )}
        {showTabs && (
          <Flex gap="1" className="overflow-x-auto">
            {assets.map((asset, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`rounded px-2 py-0.5 text-xs transition-colors ${
                  i === activeTab
                    ? "bg-puntt-accent-3 font-medium text-puntt-accent-11"
                    : "text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-3"
                }`}
              >
                {asset.name ?? `File ${i + 1}`}
              </button>
            ))}
          </Flex>
        )}
      </div>

      {/* Content */}
      {allImages ? (
        <ImageViewer assets={assets} highlightPosition={highlightPosition} />
      ) : isPdf(currentAsset) ? (
        <PdfViewer
          key={currentAsset.source}
          url={currentAsset.source}
          highlightPosition={highlightPosition}
          onPageChange={(setter) => {
            pageNavigatorRef.current = setter;
          }}
        />
      ) : isImage(currentAsset) ? (
        <ImageViewer
          assets={[currentAsset]}
          highlightPosition={highlightPosition}
        />
      ) : (
        // Fallback: try rendering as image
        <ImageViewer
          assets={[currentAsset]}
          highlightPosition={highlightPosition}
        />
      )}
    </div>
  );
});
